import React, { useContext, useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { Form } from 'semantic-ui-react';

import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import { useSelector } from 'react-redux';

import VideoPlayerEditor from 'components/modules/video/VideoPlayerEditor';
import {
  SOURCE_VIMEO,
  VIDEO_SOURCES_OPTIONS,
} from 'components/modules/video/VideoPlayerEditor/VideoPlayerEditor.constants';
import { selectBusinessFiles } from 'selectors/business';

import { TILE_TYPE_CONTENT, TILE_TYPE_VIDEO } from './TileConfiguration.constants';
import MarkdownField from '../../../../../../fields/MarkdownField';
import { CustomHeroContext } from '../../../../../custom-pages/contexts/CustomHero.context';
import { getTileClassName, getTileContent, getTileVideoData } from '../../../../services/tile';
import { TilePropType } from '../../../../types/Tile';
import { CustomHeroFormContext } from '../../CustomHeroForm.context';
import TileBackgroundConfiguration from '../TileBackgroundConfiguration';
import TileInteractionsConfiguration from '../TileInteractionsConfiguration';

import './TileConfiguration.scss';

const propTypes = {
  tileIndex: PropTypes.number.isRequired,
  tile: TilePropType.isRequired,
};

const defaultProps = {};

export default function TileConfiguration({ tileIndex, tile }) {
  const {
    setTileContent,
    setClassName,
    setVideo,
    setTileType: setHeroTileType,
  } = useContext(CustomHeroFormContext);
  const { displayVideoPlayerEditor } = useContext(CustomHeroContext);
  const businessFiles = useSelector(selectBusinessFiles);

  const contentComponentConfiguration = getTileContent(tile);
  const className = getTileClassName(tile);
  const videoData = getTileVideoData(tile);

  const [tileType, setTileType] = useState(
    !_isEmpty(videoData) ? TILE_TYPE_VIDEO : TILE_TYPE_CONTENT,
  );
  const { videoId, source, url, placeholder } = videoData;
  const videoSources = VIDEO_SOURCES_OPTIONS.map(({ value }) => value);

  const { content = '' } = contentComponentConfiguration;
  const placeholderFile = businessFiles.find(({ file }) => file === placeholder);

  function onContentChange(newContent) {
    setTileContent({ tileIndex }, { ...contentComponentConfiguration, content: newContent });
  }

  function onClassNameChange(e, { value: newClassName }) {
    setClassName({ tileIndex }, { className: newClassName });
  }

  function onTileTypeChange(_, { value: newTileType }) {
    setTileType(newTileType);
    setHeroTileType({ tileIndex }, { tileType: newTileType });
  }

  function onVideoDataChange(_, changes) {
    const finalChanges = { ...changes };
    if (_get(changes, 'placeholderFile')) {
      const file = _get(changes, 'placeholderFile');
      delete finalChanges.placeholderFile;
      finalChanges.placeholder = file.file;
    }
    const finalVideoData = { ...videoData, ...finalChanges };
    if (!_get(finalVideoData, 'source')) {
      finalVideoData.source = SOURCE_VIMEO;
    }
    setVideo({ tileIndex }, finalVideoData);
  }

  useEffect(() => {
    if (tileType === TILE_TYPE_VIDEO && !source) {
      onVideoDataChange(null, { source: SOURCE_VIMEO });
    }
  }, [tileType, source]);

  return (
    <div className="tile-configuration">
      <Form.Input
        className="small"
        label="Class Name"
        placeholder="Enter a class name here"
        value={className}
        onChange={onClassNameChange}
      />
      {displayVideoPlayerEditor && (
        <>
          <div className="field-label">Tile Type</div>
          <Form.Group inline className="tile-type-selector">
            <Form.Field key="tile-type-content">
              <Form.Radio
                label="Image and/or Content"
                value={TILE_TYPE_CONTENT}
                checked={tileType === TILE_TYPE_CONTENT}
                onClick={onTileTypeChange}
              />
            </Form.Field>
            <Form.Field key="tile-type-video">
              <Form.Radio
                label="Video"
                value={TILE_TYPE_VIDEO}
                checked={tileType === TILE_TYPE_VIDEO}
                onClick={onTileTypeChange}
              />
            </Form.Field>
          </Form.Group>
        </>
      )}
      {tileType === TILE_TYPE_CONTENT && (
        <>
          <Form.Field>
            <label htmlFor={`content-${tileIndex}`}>Content</label>
            <MarkdownField
              id={`content-${tileIndex}`}
              value={content}
              onChange={onContentChange}
              options={{
                placeholder: 'Enter a tile content here',
                minHeight: '200px',
              }}
            />
          </Form.Field>
          <TileBackgroundConfiguration tileIndex={tileIndex} tile={tile} />
          <TileInteractionsConfiguration tileIndex={tileIndex} tile={tile} />
        </>
      )}
      {tileType === TILE_TYPE_VIDEO && (
        <VideoPlayerEditor
          videoSources={videoSources}
          onChange={onVideoDataChange}
          placeholderFile={placeholderFile}
          videoId={videoId}
          source={source}
          url={url}
        />
      )}
    </div>
  );
}

TileConfiguration.propTypes = propTypes;
TileConfiguration.defaultProps = defaultProps;
